import { useQueries } from "@tanstack/react-query";
import { Account } from "@wingspanhq/wingspan-api-sdk";
import { queriesAccounts } from "@query";
import uniq from "lodash/uniq";

/**
 * Resolve the Accounts behind a consent screen's candidates into a lookup, so
 * the picker can show names rather than raw account ids.
 *
 * The authorize context carries `accountId` only, and `listAccounts` cannot
 * filter by a set of ids, so each distinct account is read with `getAccount`.
 * `useQueries` fans the reads out in parallel keyed by the registry cache key,
 * so they dedupe with (and prime) `queriesAccounts.getAccount.useQuery`.
 *
 * NOTE: `src/modules/Paystubs/hooks/useQueryPayerAccounts.ts` does the same
 * thing for the paystub history. Its implementation is generic but its name is
 * not, so this stays separate rather than importing a "payer" hook into the
 * consent flow. Worth hoisting into a shared hook if a third caller appears.
 */
export const useQueryCandidateAccounts = (
  accountIds: string[]
): { accountsById: Map<string, Account>; isLoading: boolean } => {
  const ids = uniq(accountIds.filter(Boolean));

  const results = useQueries({
    queries: ids.map(accountId => ({
      queryKey: queriesAccounts.getAccount.getQueryKey({ accountId }),
      queryFn: () => queriesAccounts.getAccount.fetchQueryData({ accountId })
    }))
  });

  const accountsById = new Map<string, Account>();
  results.forEach((result, index) => {
    if (result.data) {
      accountsById.set(ids[index], result.data);
    }
  });

  return {
    accountsById,
    isLoading: results.some(result => result.isLoading)
  };
};

/** Display name for a candidate Account, falling back to its id. */
export const candidateAccountName = (
  accountId: string,
  accountsById: Map<string, Account>
): string => accountsById.get(accountId)?.profile?.displayName || accountId;
